import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const listSchema = z.object({
  pageKey: z.string().trim().min(1).max(200),
  offset: z.number().int().min(0).max(10000).optional(),
  limit: z.number().int().min(1).max(50).optional(),
});

const postSchema = z.object({
  pageKey: z.string().trim().min(1).max(200),
  pagePath: z.string().trim().max(300).optional().or(z.literal("")),
  videoTitle: z.string().trim().max(300).optional().or(z.literal("")),
  authorName: z.string().trim().min(2).max(60),
  body: z.string().trim().min(5).max(2000),
  captchaToken: z.string().trim().max(300),
  captchaAnswer: z.string().trim().max(10),
  website: z.string().max(200).optional().or(z.literal("")),
});

export type PublicComment = {
  id: string;
  authorName: string;
  body: string;
  createdAt: string;
};

/** Nombre de commentaires affichés par page. */
export const COMMENTS_PAGE_SIZE = 10;

export type CommentPage = {
  items: PublicComment[];
  total: number;
  offset: number;
  limit: number;
  hasMore: boolean;
};

/** Question anti-spam signée pour le fil de commentaires. */
export const getCommentChallenge = createServerFn({ method: "GET" }).handler(async () => {
  const { createChallenge } = await import("@/lib/contact-captcha.server");
  return createChallenge();
});

/** Liste les commentaires publiés d'une vidéo. */
export const listComments = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => listSchema.parse(data))
  .handler(async ({ data }): Promise<CommentPage> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const offset = data.offset ?? 0;
    const limit = data.limit ?? COMMENTS_PAGE_SIZE;
    const { data: rows, count } = await supabaseAdmin
      .from("video_comments")
      .select("id, author_name, body, created_at", { count: "exact" })
      .eq("page_key", data.pageKey)
      .eq("status", "published")
      .order("created_at", { ascending: true })
      .range(offset, offset + limit - 1);
    const items = (rows ?? []).map((r) => ({
      id: r.id as string,
      authorName: r.author_name as string,
      body: r.body as string,
      createdAt: r.created_at as string,
    }));
    const total = count ?? items.length;
    return { items, total, offset, limit, hasMore: offset + items.length < total };
  });

/** Publie un commentaire (anti-spam : piège, captcha signé, limitation par page). */
export const postComment = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => postSchema.parse(data))
  .handler(async ({ data }) => {
    if (data.website && data.website.trim().length > 0) {
      return { ok: true as const, message: "Commentaire publié." };
    }

    const { verifyChallenge } = await import("@/lib/contact-captcha.server");
    if (!verifyChallenge(data.captchaToken, data.captchaAnswer)) {
      return {
        ok: false as const,
        message: "Réponse anti-spam incorrecte ou expirée. Recommencez le calcul.",
      };
    }

    if (/https?:\/\//i.test(data.body)) {
      return { ok: false as const, message: "Les liens ne sont pas autorisés dans les commentaires." };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Limitation : 5 commentaires par heure sur une même page.
    const since = new Date(Date.now() - 60 * 60 * 1000).toISOString();
    const { count } = await supabaseAdmin
      .from("video_comments")
      .select("id", { count: "exact", head: true })
      .eq("page_key", data.pageKey)
      .gte("created_at", since);
    if ((count ?? 0) >= 5) {
      return {
        ok: false as const,
        message: "Trop de commentaires récents sur cette page. Réessayez dans une heure.",
      };
    }

    const { error } = await supabaseAdmin.from("video_comments").insert({
      page_key: data.pageKey,
      page_path: data.pagePath || null,
      video_title: data.videoTitle || null,
      author_name: data.authorName,
      body: data.body,
      status: "published",
    });
    if (error) {
      return { ok: false as const, message: "Publication impossible pour le moment, réessayez." };
    }
    return { ok: true as const, message: "Commentaire publié." };
  });
